import { Shield, HelpCircle, CreditCard, Phone, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Hero } from '../../components/Hero';

const insuranceProviders = [
  {
    name: 'Aetna',
    logo: 'https://logos-world.net/wp-content/uploads/2021/12/Aetna-Logo.png',
  },
  {
    name: 'Blue Cross Blue Shield',
    logo: 'https://www.villagemd.com/hubfs/news/BCBSGa.jpg',
  },
  {
    name: 'Cigna',
    logo: 'https://logo-marque.com/wp-content/uploads/2022/03/Cigna-Logo-2011.jpg',
  },
  {
    name: 'Humana',
    logo: 'https://www.medicaresupplements4less.com/wp-content/uploads/2018/03/PNGPIX-COM-Humana-Logo-PNG-Transparent-1024x269.png',
  },
  {
    name: 'Medicare',
    logo: 'https://logos-world.net/wp-content/uploads/2021/02/Medicare-Symbol.png',
  },
  {
    name: 'United Healthcare',
    logo: 'https://seekvectorlogo.com/wp-content/uploads/2021/12/unitedhealthcare-vector-logo-2021.png',
  },
  {
    name: 'WellCare',
    logo: 'https://purepng.com/public/uploads/large/purepng.com-wellcare-health-plans-logologobrand-logoiconslogos-2515199384993yy1h.png',
  },
  {
    name: 'Tricare',
    logo: 'https://www.ngaus.org/sites/default/files/styles/cover_image/public/2019-01/012219tricare1000_edited_0.jpg',
  },
  {
    name: 'Peach State Medicaid',
    logo: 'https://mma.prnewswire.com/media/2273069/Peach_State_Health_Plan_Logo.jpg',
  },
  {
    name: 'Oscar',
    logo: 'https://res.cloudinary.com/value-penguin/image/upload/c_limit/dpr_1.0/f_auto/h_1600/q_auto/w_1600/v1/referral_logos/us/life_insurance/oscar-1',
  },
  {
    name: 'Anthem',
    logo: 'https://static-22.sinclairstoryline.com/resources/media/b0e80d4e-652e-42f2-b3a6-14f67d65ecde-large16x9_AnthemhealthinsuranceMGN2.jpg',
  }
];

export function Insurance() {
  return (
    <div>
      <Hero
        title="Insurance Information"
        description="We work with most major insurance providers to ensure you receive the care you need. Our team is here to help you understand your coverage and benefits."
        image="https://images.unsplash.com/photo-1563013544-824ae1b704d3?auto=format&fit=crop&q=80"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Verification Notice */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-12 rounded-lg">
          <div className="flex items-start">
            <HelpCircle className="h-6 w-6 text-yellow-400 mt-1 mr-3" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Important Insurance Notice</h2>
              <p className="text-gray-700">
                Please contact our insurance specialist at{' '}
                <a href="tel:+14702393602" className="text-blue-600 hover:text-blue-800 font-medium">
                  (470) 239-3602
                </a>{' '}
                to verify your specific insurance coverage and network status. Insurance networks and coverage 
                details can change, and verification is required to ensure your visit will be covered.
              </p>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Accepted Insurance Plans</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {insuranceProviders.map((provider) => (
              <div 
                key={provider.name}
                className="bg-white rounded-lg p-6 shadow-md flex flex-col items-center justify-center text-center"
              >
                <div className="w-64 h-48 bg-gray-50 rounded-lg flex items-center justify-center p-6 mb-4">
                  <img 
                    src={provider.logo} 
                    alt={`${provider.name} logo`}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                <p className="font-medium text-gray-900">{provider.name}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-12 mb-16">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Insurance Process</h2>
            <div className="bg-blue-50 rounded-lg p-8">
              <ul className="space-y-6">
                <li className="flex items-start">
                  <Shield className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Verification of Benefits</h4>
                    <p className="text-gray-600">Our team will verify your coverage before your appointment</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <FileText className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Prior Authorization</h4>
                    <p className="text-gray-600">We handle necessary pre-authorizations for procedures</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <CreditCard className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Payment Options</h4>
                    <p className="text-gray-600">Flexible payment plans available for out-of-pocket expenses</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Important Information</h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Referrals</h3>
                <p className="text-gray-600 mb-4">
                  Some insurance plans require a referral from your primary care physician. 
                  Please check with your insurance provider or contact our office to verify 
                  referral requirements.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Co-Payments</h3>
                <p className="text-gray-600 mb-4">
                  Co-payments are due at the time of service. We accept cash, checks, 
                  and most major credit cards for your convenience.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Privacy Notice</h3>
                <p className="text-gray-600 mb-4">
                  We are committed to protecting your privacy and ensuring the security of your 
                  health information. Please review our HIPAA Privacy Notice for details.
                </p>
                <Link
                  to="/legal/hipaa-privacy-notice"
                  className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center"
                >
                  <Shield className="h-5 w-5 mr-2" />
                  View HIPAA Privacy Notice
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Need Help?</h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Our insurance specialists are here to help you understand your coverage and benefits. 
            Contact us for assistance with verifying your insurance or answering any questions.
          </p>
          <div className="flex justify-center gap-4">
            <a
              href="tel:+14702393602"
              className="bg-blue-600 text-white px-8 py-3 rounded-md font-semibold hover:bg-blue-700 transition-colors inline-flex items-center"
            >
              <Phone className="h-5 w-5 mr-2" />
              Contact Insurance Specialist
            </a>
            <Link
              to="/legal/hipaa-privacy-notice"
              className="bg-white text-blue-600 border border-blue-600 px-8 py-3 rounded-md font-semibold hover:bg-blue-50 transition-colors inline-flex items-center"
            >
              <Shield className="h-5 w-5 mr-2" />
              Privacy Notice
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}